/** Row shape from companySiteRoleJobs API (camelCase or PascalCase). */
export type CompanySiteRoleJobRow = {
  companyTestTypeId?: number;
  CompanyTestTypeId?: number;
  siteId?: number;
  SiteId?: number;
  siteName?: string;
  SiteName?: string;
  jobIds?: string;
  JobIds?: string;
  jobId?: number;
  JobId?: number;
};

export type FormSelectionSite = {
  siteId: number;
  siteName: string;
};

export type FormSelectionJob = {
  id: number;
  name: string;
};

export type FormSelectionFilterOptions = {
  companyTestTypeId?: number;
  /** Site already chosen (form data or saved on test instance). */
  selectedSiteId?: number | null;
  /** Site locked by proctor on a prior step. */
  lockedSiteId?: number | null;
  /** Job locked by proctor on a prior step — narrows site list. */
  lockedJobId?: number | null;
  /** Department chosen on the request form — narrows job list. */
  selectedDepartmentId?: number | null;
  /** Departments linked to the active CTT — limits jobs when non-empty. */
  cttDepartmentIds?: number[] | null;
  /** When true and no site is selected, intersect job IDs across CSRJ sites (request page uses union by default). */
  intersectJobsAcrossSites?: boolean;
};

type CompanyJobLookup = {
  id?: number;
  name?: string;
  departmentId?: number | null;
  DepartmentId?: number | null;
  departmentIds?: number[] | null;
  DepartmentIds?: number[] | null;
};

export type FormSelectionDepartment = {
  id: number;
  name: string;
};

function jobDepartmentIds(job: CompanyJobLookup | undefined): number[] {
  if (!job) return [];
  const list = job.departmentIds ?? job.DepartmentIds;
  if (Array.isArray(list) && list.length > 0) {
    return list.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  }
  const single = job.departmentId ?? job.DepartmentId;
  const id = Number(single);
  return Number.isFinite(id) && id > 0 ? [id] : [];
}

function jobDepartmentId(job: CompanyJobLookup | undefined): number | null {
  const ids = jobDepartmentIds(job);
  return ids.length > 0 ? ids[0] : null;
}

function jobMatchesDepartment(job: CompanyJobLookup | undefined, departmentId: number): boolean {
  return jobDepartmentIds(job).includes(departmentId);
}

function rowCttId(row: CompanySiteRoleJobRow): number {
  return Number(row.companyTestTypeId ?? row.CompanyTestTypeId ?? 0);
}

export function hasCompanySiteRoleJobsForCtt(
  rows: CompanySiteRoleJobRow[] | null | undefined,
  companyTestTypeId?: number | null
): boolean {
  if (!companyTestTypeId || companyTestTypeId <= 0) {
    return false;
  }
  return (rows ?? []).some((row) => rowCttId(row) === companyTestTypeId);
}

/** Replace CSRJ rows for one CTT while preserving rows for other test types. */
export function mergeCompanySiteRoleJobsForCtt(
  existing: CompanySiteRoleJobRow[] | null | undefined,
  companyTestTypeId: number,
  newRows: CompanySiteRoleJobRow[] | null | undefined
): CompanySiteRoleJobRow[] {
  const kept = (existing ?? []).filter((row) => rowCttId(row) !== companyTestTypeId);
  return [...kept, ...(Array.isArray(newRows) ? newRows : [])];
}

function rowSiteId(row: CompanySiteRoleJobRow): number {
  return Number(row.siteId ?? row.SiteId ?? 0);
}

function rowSiteName(row: CompanySiteRoleJobRow): string {
  return String(row.siteName ?? row.SiteName ?? "").trim();
}

/** Parse comma-delimited JobIds from a CSRJ row. */
export function parseJobIdsFromRow(row: CompanySiteRoleJobRow): number[] {
  const raw = row.jobIds ?? row.JobIds;
  if (raw) {
    return String(raw)
      .split(",")
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !Number.isNaN(id) && id > 0);
  }
  const single = row.jobId ?? row.JobId;
  if (single != null && Number(single) > 0) {
    return [Number(single)];
  }
  return [];
}

export function filterRowsForCtt(
  rows: CompanySiteRoleJobRow[] | null | undefined,
  companyTestTypeId?: number | null
): CompanySiteRoleJobRow[] {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }
  if (!companyTestTypeId || companyTestTypeId <= 0) {
    return rows;
  }
  return rows.filter((row) => rowCttId(row) === companyTestTypeId);
}

function rowHasJobId(row: CompanySiteRoleJobRow, jobId: number): boolean {
  return parseJobIdsFromRow(row).includes(jobId);
}

function applyLockedJobFilter(
  rows: CompanySiteRoleJobRow[],
  lockedJobId?: number | null
): CompanySiteRoleJobRow[] {
  if (!lockedJobId || lockedJobId <= 0) {
    return rows;
  }
  return rows.filter((row) => rowHasJobId(row, lockedJobId));
}

function jobIdsBySiteFromRows(rows: CompanySiteRoleJobRow[]): Map<number, Set<number>> {
  const jobsBySite = new Map<number, Set<number>>();
  rows.forEach((row) => {
    const siteId = rowSiteId(row);
    if (siteId <= 0) {
      return;
    }
    const siteJobs = jobsBySite.get(siteId) ?? new Set<number>();
    parseJobIdsFromRow(row).forEach((id) => siteJobs.add(id));
    jobsBySite.set(siteId, siteJobs);
  });
  return jobsBySite;
}

/** Jobs present at every site in CSRJ rows (union jobs per site, then intersect across sites). */
export function intersectJobIdsAcrossSites(rows: CompanySiteRoleJobRow[]): number[] {
  const jobsBySite = jobIdsBySiteFromRows(rows);
  if (jobsBySite.size === 0) {
    return [];
  }

  let intersection: Set<number> | null = null;
  for (const siteJobs of jobsBySite.values()) {
    if (intersection === null) {
      intersection = new Set(siteJobs);
      continue;
    }
    intersection = new Set([...intersection].filter((id) => siteJobs.has(id)));
    if (intersection.size === 0) {
      break;
    }
  }

  return Array.from(intersection ?? []).sort((a, b) => a - b);
}

function applyDepartmentFilters(
  jobIds: number[],
  companyJobs: CompanyJobLookup[] | null | undefined,
  options: FormSelectionFilterOptions
): number[] {
  if (!Array.isArray(companyJobs) || jobIds.length === 0) {
    return jobIds;
  }

  let result = jobIds;

  if (options.selectedDepartmentId && options.selectedDepartmentId > 0) {
    const allowed = new Set(
      companyJobs
        .filter((job) => jobMatchesDepartment(job, options.selectedDepartmentId!))
        .map((job) => Number(job.id))
        .filter((id) => id > 0)
    );
    result = result.filter((id) => allowed.has(id));
  }

  const cttDeptIds = options.cttDepartmentIds?.filter((id) => id > 0) ?? [];
  if (cttDeptIds.length > 0) {
    const cttAllowed = new Set(cttDeptIds);
    const allowed = new Set(
      companyJobs
        .filter((job) => {
          const deptIds = jobDepartmentIds(job);
          return deptIds.some((deptId) => cttAllowed.has(deptId));
        })
        .map((job) => Number(job.id))
        .filter((id) => id > 0)
    );
    result = result.filter((id) => allowed.has(id));
  }

  return result;
}

type SiteSimpleLike = {
  id?: number;
  Id?: number;
  siteId?: number;
  SiteId?: number;
  name?: string;
  Name?: string;
  siteName?: string;
  SiteName?: string;
  alias?: string;
  Alias?: string;
  siteAlias?: string;
  SiteAlias?: string;
};

function siteSimpleId(site: SiteSimpleLike | null | undefined): number {
  return Number(site?.id ?? site?.Id ?? site?.siteId ?? site?.SiteId ?? 0);
}

function siteSimpleDisplayName(site: SiteSimpleLike | null | undefined, siteId: number): string {
  const alias = String(
    site?.alias ?? site?.Alias ?? site?.siteAlias ?? site?.SiteAlias ?? "",
  ).trim();
  const name = String(
    site?.name ?? site?.Name ?? site?.siteName ?? site?.SiteName ?? "",
  ).trim();
  return alias || name || String(siteId);
}

function buildSiteNameLookup(
  ...sources: (SiteSimpleLike[] | null | undefined)[]
): Map<number, string> {
  const byId = new Map<number, string>();
  sources.forEach((list) => {
    (list ?? []).forEach((site) => {
      const siteId = siteSimpleId(site);
      if (siteId <= 0 || byId.has(siteId)) {
        return;
      }
      const alias = String(
        site?.alias ?? site?.Alias ?? site?.siteAlias ?? site?.SiteAlias ?? "",
      ).trim();
      const name = String(
        site?.name ?? site?.Name ?? site?.siteName ?? site?.SiteName ?? "",
      ).trim();
      const display = alias || name;
      if (display) {
        byId.set(siteId, display);
      }
    });
  });
  return byId;
}

function enrichSiteNamesFromLookups(
  sites: FormSelectionSite[],
  nameById: Map<number, string>,
): FormSelectionSite[] {
  return sites.map((site) => {
    const resolved = nameById.get(site.siteId);
    if (!resolved) {
      return site;
    }
    const isIdFallback = !site.siteName || site.siteName === String(site.siteId);
    return isIdFallback ? { ...site, siteName: resolved } : site;
  });
}

function siteNameLookupSources(
  options: RequestSiteContextOptions,
): (SiteSimpleLike[] | null | undefined)[] {
  const csrjSites = (options.companySiteRoleJobs ?? []).map((row) => ({
    siteId: rowSiteId(row),
    siteName: rowSiteName(row),
  }));
  return [
    options.testType?.sites,
    options.testType?.permissionRoleSitesForCreate,
    options.testType?.permissionRoleSitesForTake,
    options.testType?.eligibleSitesForProctorSiteSelect,
    options.testType?.eligibleSitesForTakerSiteSelect,
    options.userRoleSites,
    options.eligibleSitesFromServer,
    csrjSites,
  ];
}

export function isSiteRequestField(field: { fieldElementName?: string } | null | undefined): boolean {
  const name = String(field?.fieldElementName ?? "");
  return name === "Site Select" || name === "Location Selection";
}

/** Proctor/creator sites from the active CTT (SiteTestTypes). */
export function getSitesFromTestType(
  testType: { sites?: SiteSimpleLike[] } | null | undefined
): FormSelectionSite[] {
  if (!testType || !Array.isArray(testType.sites)) {
    return [];
  }
  const byId = new Map<number, FormSelectionSite>();
  testType.sites.forEach((site) => {
    const siteId = siteSimpleId(site);
    if (siteId <= 0 || byId.has(siteId)) {
      return;
    }
    byId.set(siteId, { siteId, siteName: siteSimpleDisplayName(site, siteId) });
  });
  return Array.from(byId.values()).sort((a, b) => a.siteName.localeCompare(b.siteName));
}

export type RequestSiteContextOptions = {
  /** @deprecated Prefer siteFieldProctorProvided — kept for callers not tied to a specific field. */
  isProctorContext?: boolean;
  /** When true, Site Select options use proctor (CanCreate) union scope. */
  siteFieldProctorProvided?: boolean;
  testType?: { sites?: SiteSimpleLike[] | null; eligibleSitesForProctorSiteSelect?: SiteSimpleLike[] | null; eligibleSitesForTakerSiteSelect?: SiteSimpleLike[] | null; permissionRoleSitesForCreate?: SiteSimpleLike[] | null; permissionRoleSitesForTake?: SiteSimpleLike[] | null } | null;
  /** Sites the current user can access via their company roles (Set 3 for proctor scope). */
  userRoleSites?: SiteSimpleLike[] | null;
  /** Precomputed union from test state when CTT is not yet selected. */
  eligibleSitesFromServer?: SiteSimpleLike[] | null;
  companySiteRoleJobs?: CompanySiteRoleJobRow[] | null;
  filterOptions?: FormSelectionFilterOptions;
  efTests?: EfTestLike[] | null;
  isEfTestType?: boolean;
  jobId?: number | null;
  /** When true, keep only sites that have at least one eligible job (required Job Select for same actor). */
  requireSitesWithJobs?: boolean;
  companyJobs?: CompanyJobLookup[] | null;
};

/** Union of site lists, deduped by siteId. Later lists fill missing names. */
export function unionSiteLists(...siteLists: FormSelectionSite[][]): FormSelectionSite[] {
  const byId = new Map<number, FormSelectionSite>();
  siteLists.forEach((sites) => {
    (sites ?? []).forEach((site) => {
      if (!site?.siteId || site.siteId <= 0) {
        return;
      }
      const existing = byId.get(site.siteId);
      if (!existing) {
        byId.set(site.siteId, { siteId: site.siteId, siteName: site.siteName || String(site.siteId) });
        return;
      }
      if (!existing.siteName && site.siteName) {
        byId.set(site.siteId, { siteId: site.siteId, siteName: site.siteName });
      }
    });
  });
  return Array.from(byId.values()).sort((a, b) => a.siteName.localeCompare(b.siteName));
}

/**
 * Intersection of site lists by siteId. Empty lists are skipped (no constraint).
 * Merges the best available name from any contributing list.
 */
export function intersectSiteLists(...siteLists: FormSelectionSite[][]): FormSelectionSite[] {
  const activeLists = siteLists.filter((list) => Array.isArray(list) && list.length > 0);
  if (activeLists.length === 0) {
    return [];
  }

  let ids = new Set(
    activeLists[0]
      .map((site) => site.siteId)
      .filter((siteId) => siteId > 0),
  );

  for (let i = 1; i < activeLists.length; i += 1) {
    const nextIds = new Set(
      activeLists[i]
        .map((site) => site.siteId)
        .filter((siteId) => siteId > 0),
    );
    ids = new Set([...ids].filter((siteId) => nextIds.has(siteId)));
    if (ids.size === 0) {
      return [];
    }
  }

  const nameById = new Map<number, string>();
  activeLists.forEach((list) => {
    list.forEach((site) => {
      if (site.siteId > 0 && site.siteName && !nameById.has(site.siteId)) {
        nameById.set(site.siteId, site.siteName);
      }
    });
  });

  return [...ids]
    .map((siteId) => ({
      siteId,
      siteName: nameById.get(siteId) || String(siteId),
    }))
    .sort((a, b) => a.siteName.localeCompare(b.siteName));
}

function sitesFromServerPayload(sites: SiteSimpleLike[] | null | undefined): FormSelectionSite[] {
  return getSitesFromTestType({ sites: sites ?? undefined });
}

function resolveServerEligibleSites(
  options: RequestSiteContextOptions,
  proctorProvided: boolean
): FormSelectionSite[] | null {
  const fromServer = options.eligibleSitesFromServer;
  if (fromServer != null && fromServer.length > 0) {
    return sitesFromServerPayload(fromServer);
  }
  const fromTestType = proctorProvided
    ? options.testType?.eligibleSitesForProctorSiteSelect
    : options.testType?.eligibleSitesForTakerSiteSelect;
  if (fromTestType != null && fromTestType.length > 0) {
    return sitesFromServerPayload(fromTestType);
  }
  return null;
}

function getIntersectSitesForRequest(options: RequestSiteContextOptions): FormSelectionSite[] {
  const proctorProvided = options.siteFieldProctorProvided === true
    || (options.siteFieldProctorProvided !== false && Boolean(options.isProctorContext));

  const serverSites = resolveServerEligibleSites(options, proctorProvided);
  if (serverSites != null) {
    return serverSites;
  }

  const set1 = getSitesFromTestType(options.testType);
  const set2 = getSitesFromTestType({
    sites: proctorProvided
      ? options.testType?.permissionRoleSitesForCreate
      : options.testType?.permissionRoleSitesForTake,
  });
  const set3 = getSitesFromTestType({ sites: options.userRoleSites ?? undefined });
  return intersectSiteLists(set1, set2, set3);
}

/** Keep only sites where the actor can pick at least one job (when Job Select is required). */
export function filterSitesToThoseWithEligibleJobs(
  sites: FormSelectionSite[],
  options: RequestSiteContextOptions
): FormSelectionSite[] {
  if (sites.length === 0 || !options.requireSitesWithJobs) {
    return sites;
  }

  const filterOpts = options.filterOptions ?? {};
  return sites.filter((site) => {
    const perSiteOpts = { ...filterOpts, selectedSiteId: site.siteId };
    if (options.isEfTestType) {
      return getEfJobIdsForRequest(
        options.efTests,
        options.companySiteRoleJobs,
        perSiteOpts
      ).length > 0;
    }
    return getJobIdsFromCsrj(
      options.companySiteRoleJobs,
      perSiteOpts,
      options.companyJobs
    ).length > 0;
  });
}

/** Sites available on the request form for the current actor (intersection of CTT, permission-role, and actor-role sites). */
export function getSitesForRequestContext(options: RequestSiteContextOptions): FormSelectionSite[] {
  const filterOptions = options.filterOptions ?? {};
  let sites = getIntersectSitesForRequest(options);

  if (options.isEfTestType) {
    sites = getEfSitesForRequest(
      options.efTests,
      options.companySiteRoleJobs,
      filterOptions,
      options.jobId ?? null,
      sites
    );
  }

  const nameById = buildSiteNameLookup(...siteNameLookupSources(options));
  sites = enrichSiteNamesFromLookups(sites, nameById);

  return filterSitesToThoseWithEligibleJobs(sites, options);
}

/** Unique taker-valid sites from CSRJ (never actor-scoped CTT sites). */
export function getSitesFromCsrj(
  rows: CompanySiteRoleJobRow[] | null | undefined,
  options: FormSelectionFilterOptions = {}
): FormSelectionSite[] {
  let filtered = filterRowsForCtt(rows, options.companyTestTypeId);
  filtered = applyLockedJobFilter(filtered, options.lockedJobId);

  const byId = new Map<number, FormSelectionSite>();
  filtered.forEach((row) => {
    const siteId = rowSiteId(row);
    if (siteId <= 0) return;
    if (!byId.has(siteId)) {
      byId.set(siteId, { siteId, siteName: rowSiteName(row) });
    }
  });
  return Array.from(byId.values()).sort((a, b) => a.siteName.localeCompare(b.siteName));
}

/** Union of taker-valid job ids for the effective site context. */
export function getJobIdsFromCsrj(
  rows: CompanySiteRoleJobRow[] | null | undefined,
  options: FormSelectionFilterOptions = {},
  companyJobs?: CompanyJobLookup[] | null
): number[] {
  let filtered = filterRowsForCtt(rows, options.companyTestTypeId);
  filtered = applyLockedJobFilter(filtered, options.lockedJobId);

  const effectiveSiteId =
    options.selectedSiteId && options.selectedSiteId > 0
      ? options.selectedSiteId
      : options.lockedSiteId && options.lockedSiteId > 0
        ? options.lockedSiteId
        : null;

  if (options.lockedJobId && options.lockedJobId > 0) {
    if (effectiveSiteId) {
      const atSite = filtered.some(
        (row) => rowSiteId(row) === effectiveSiteId && rowHasJobId(row, options.lockedJobId!)
      );
      return atSite ? [options.lockedJobId] : [];
    }
    return filtered.some((row) => rowHasJobId(row, options.lockedJobId!))
      ? [options.lockedJobId]
      : [];
  }

  if (effectiveSiteId) {
    filtered = filtered.filter((row) => rowSiteId(row) === effectiveSiteId);
  }

  let result: number[];
  if (!effectiveSiteId && options.intersectJobsAcrossSites) {
    result = intersectJobIdsAcrossSites(filtered);
  } else {
    const ids = new Set<number>();
    filtered.forEach((row) => parseJobIdsFromRow(row).forEach((id) => ids.add(id)));
    result = Array.from(ids).sort((a, b) => a - b);
  }

  return applyDepartmentFilters(result, companyJobs, options);
}

/** True when CSRJ rows exist for the CTT and at least one eligible job id can be resolved. */
export function hasUsableCompanySiteRoleJobsForCtt(
  rows: CompanySiteRoleJobRow[] | null | undefined,
  companyTestTypeId?: number | null
): boolean {
  if (!hasCompanySiteRoleJobsForCtt(rows, companyTestTypeId)) {
    return false;
  }
  return getJobIdsFromCsrj(rows, { companyTestTypeId }).length > 0;
}

export function getJobsFromCsrj(
  rows: CompanySiteRoleJobRow[] | null | undefined,
  options: FormSelectionFilterOptions,
  companyJobs: CompanyJobLookup[] | null | undefined
): FormSelectionJob[] {
  const jobIds = getJobIdsFromCsrj(rows, options, companyJobs);
  if (!Array.isArray(companyJobs) || jobIds.length === 0) {
    return [];
  }
  return jobIds
    .map((id) => {
      const job = companyJobs.find((j) => Number(j.id) === id);
      return job?.name ? { id, name: job.name } : null;
    })
    .filter((job): job is FormSelectionJob => job != null)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Departments that have at least one eligible job for the current site context. */
export function getDepartmentsFromEligibleJobs(
  rows: CompanySiteRoleJobRow[] | null | undefined,
  options: FormSelectionFilterOptions,
  companyJobs: CompanyJobLookup[] | null | undefined,
  departments: Array<{ id?: number; name?: string; active?: boolean; Active?: boolean }> | null | undefined
): FormSelectionDepartment[] {
  const jobIds = getJobIdsFromCsrj(
    rows,
    { ...options, selectedDepartmentId: null },
    companyJobs
  );
  if (jobIds.length === 0 || !Array.isArray(companyJobs)) {
    return [];
  }

  const deptIds = new Set<number>();
  jobIds.forEach((jobId) => {
    const job = companyJobs.find((j) => Number(j.id) === jobId);
    jobDepartmentIds(job).forEach((deptId) => deptIds.add(deptId));
  });

  const catalog = Array.isArray(departments) ? departments : [];
  const cttDeptIds = options.cttDepartmentIds?.filter((id) => id > 0) ?? [];
  return catalog
    .filter((d) => {
      const id = Number(d.id);
      const active = d.active ?? d.Active ?? true;
      if (!active || !deptIds.has(id)) return false;
      if (cttDeptIds.length > 0) {
        return cttDeptIds.includes(id);
      }
      return true;
    })
    .map((d) => ({ id: Number(d.id), name: String(d.name ?? "") }))
    .filter((d) => d.id > 0 && d.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Row shape from TestStateByCompany efTests (camelCase or PascalCase). */
export type EfTestLike = {
  siteId?: number;
  SiteId?: number;
  jobId?: number;
  JobId?: number;
  siteName?: string;
  SiteName?: string;
  siteAlias?: string;
  SiteAlias?: string;
};

function efSiteId(ef: EfTestLike): number {
  return Number(ef.siteId ?? ef.SiteId ?? 0);
}

function efJobId(ef: EfTestLike): number {
  return Number(ef.jobId ?? ef.JobId ?? 0);
}

function efSiteName(ef: EfTestLike): string {
  return String(ef.siteName ?? ef.SiteName ?? ef.siteAlias ?? ef.SiteAlias ?? "").trim();
}

function efSelectedSiteId(options: FormSelectionFilterOptions): number | null {
  if (options.selectedSiteId && options.selectedSiteId > 0) {
    return options.selectedSiteId;
  }
  if (options.lockedSiteId && options.lockedSiteId > 0) {
    return options.lockedSiteId;
  }
  return null;
}

/**
 * Jobs on a BE questionnaire. A job is listed when a submitted BE Source exists
 * for it, narrowed to the selected site when one is chosen.
 */
export function getEfJobIdsForRequest(
  efTests: EfTestLike[] | null | undefined,
  _rows: CompanySiteRoleJobRow[] | null | undefined,
  options: FormSelectionFilterOptions = {}
): number[] {
  const siteId = efSelectedSiteId(options);
  const ids = new Set<number>();
  (efTests ?? []).forEach((ef) => {
    const jobId = efJobId(ef);
    if (jobId <= 0) {
      return;
    }
    if (siteId && efSiteId(ef) !== siteId) {
      return;
    }
    ids.add(jobId);
  });
  return Array.from(ids).sort((a, b) => a - b);
}

/** Sites that have a submitted BE Source. */
export function getEfSitesForRequest(
  efTests: EfTestLike[] | null | undefined,
  _rows: CompanySiteRoleJobRow[] | null | undefined,
  _options: FormSelectionFilterOptions = {},
  filterByJobId?: number | null,
  _eligibleSites?: FormSelectionSite[] | null
): FormSelectionSite[] {
  const byId = new Map<number, FormSelectionSite>();

  (efTests ?? []).forEach((ef) => {
    const siteId = efSiteId(ef);
    if (siteId <= 0) {
      return;
    }
    if (filterByJobId && filterByJobId > 0 && efJobId(ef) !== filterByJobId) {
      return;
    }
    if (!byId.has(siteId)) {
      byId.set(siteId, { siteId, siteName: efSiteName(ef) });
    }
  });

  return Array.from(byId.values()).sort((a, b) => a.siteName.localeCompare(b.siteName));
}

export function getEfJobsForRequest(
  efTests: EfTestLike[] | null | undefined,
  rows: CompanySiteRoleJobRow[] | null | undefined,
  options: FormSelectionFilterOptions,
  companyJobs: CompanyJobLookup[] | null | undefined
): FormSelectionJob[] {
  const jobIds = getEfJobIdsForRequest(efTests, rows, options);
  if (!Array.isArray(companyJobs) || jobIds.length === 0) {
    return [];
  }
  return jobIds
    .map((id) => {
      const job = companyJobs.find((j) => Number(j.id) === id);
      return job?.name ? { id, name: job.name } : null;
    })
    .filter((job): job is FormSelectionJob => job != null)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** True when at least one CanTake role row supports the site (and optional job). */
export function isRoleCoherent(
  rows: CompanySiteRoleJobRow[] | null | undefined,
  options: FormSelectionFilterOptions,
  siteId: number,
  jobId?: number | null
): boolean {
  if (!siteId || siteId <= 0) {
    return false;
  }
  let filtered = filterRowsForCtt(rows, options.companyTestTypeId);
  filtered = filtered.filter((row) => rowSiteId(row) === siteId);
  if (!jobId || jobId <= 0) {
    return filtered.length > 0;
  }
  return filtered.some((row) => rowHasJobId(row, jobId));
}

type RequestFieldLike = {
  fieldId?: number;
  fieldElementName?: string;
  required?: boolean;
  proctorProvided?: boolean;
  sequence?: number;
  synthetic?: boolean;
};

export function isJobRequestField(field: RequestFieldLike): boolean {
  const fieldId = Number(field.fieldId ?? 0);
  const name = String(field.fieldElementName ?? "");
  return name === "Job Select" || name === "Job" || fieldId === 4;
}

export function isProctorProvidedJobRequestField(field: RequestFieldLike | null | undefined): boolean {
  return Boolean(field?.proctorProvided && isJobRequestField(field));
}

export function actorHasSiteSelectField(
  fields: RequestFieldLike[] | null | undefined,
  proctorProvided: boolean
): boolean {
  return (fields ?? []).some(
    (field) => field.proctorProvided === proctorProvided && isSiteRequestField(field)
  );
}

export function isJobSelectBlockedUntilSiteChosen(
  visibleFields: RequestFieldLike[] | null | undefined,
  selectedSiteId?: number | null,
  lockedSiteId?: number | null
): boolean {
  if (selectedSiteId && selectedSiteId > 0) {
    return false;
  }
  if (lockedSiteId && lockedSiteId > 0) {
    return false;
  }
  const fields = visibleFields ?? [];
  const hasSiteSelect = fields.some((field) => isSiteRequestField(field));
  const hasJobSelect = fields.some((field) => isJobRequestField(field));
  return hasSiteSelect && hasJobSelect;
}

/**
 * When Site Select (configured or synthetic) is visible and no site is chosen yet,
 * job lists may be empty until a site is picked (spec §5.4–§5.6, §6.4–§6.5).
 * Defer "No jobs available" until the user selects a site.
 */
export function shouldDeferNoJobsWarningUntilSiteSelected(
  fields: RequestFieldLike[] | null | undefined,
  siteFieldProctorProvided: boolean,
  selectedSiteId?: number | null,
  lockedSiteId?: number | null,
  visibleFields?: RequestFieldLike[] | null | undefined
): boolean {
  if (selectedSiteId && selectedSiteId > 0) {
    return false;
  }
  if (lockedSiteId && lockedSiteId > 0) {
    return false;
  }
  if (
    visibleFields &&
    isJobSelectBlockedUntilSiteChosen(visibleFields, selectedSiteId, lockedSiteId)
  ) {
    return true;
  }
  return actorHasSiteSelectField(fields, siteFieldProctorProvided);
}

export function actorHasJobSelectField(
  fields: RequestFieldLike[] | null | undefined,
  proctorProvided: boolean
): boolean {
  return (fields ?? []).some(
    (field) => field.proctorProvided === proctorProvided && isJobRequestField(field)
  );
}

/** When the actor has no Site Select field, prefer the CTT virtual site for auto-assign. */
export function resolveAutoSiteIdForRequest(options: {
  activeTestType: { sites?: SiteSimpleLike[] | null } | null | undefined;
  actorFields: RequestFieldLike[] | null | undefined;
  siteFieldProctorProvided: boolean;
  eligibleSites: FormSelectionSite[];
}): number | null {
  const { activeTestType, actorFields, siteFieldProctorProvided, eligibleSites } = options;
  if (!actorHasSiteSelectField(actorFields, siteFieldProctorProvided)) {
    const cttSites = getSitesFromTestType(activeTestType);
    if (cttSites.length === 1) {
      return cttSites[0].siteId;
    }
  }
  return eligibleSites.length === 1 ? eligibleSites[0].siteId : null;
}

/**
 * Site id used for job/department filtering: explicit form or instance selection,
 * else the sole eligible site when only one remains after intersection.
 */
export function resolveEffectiveSiteIdForRequest(options: {
  selectedSiteId?: number | null;
  lockedSiteId?: number | null;
  eligibleSites: FormSelectionSite[];
  activeTestType?: { sites?: SiteSimpleLike[] | null } | null;
  actorFields?: RequestFieldLike[] | null;
  siteFieldProctorProvided?: boolean;
}): number | null {
  const lockedSiteId = options.lockedSiteId ?? null;
  if (lockedSiteId && lockedSiteId > 0) {
    return lockedSiteId;
  }
  const selectedSiteId = options.selectedSiteId ?? null;
  if (selectedSiteId && selectedSiteId > 0) {
    return selectedSiteId;
  }
  if (options.eligibleSites.length === 1) {
    return options.eligibleSites[0].siteId;
  }
  return resolveAutoSiteIdForRequest({
    activeTestType: options.activeTestType,
    actorFields: options.actorFields ?? [],
    siteFieldProctorProvided: options.siteFieldProctorProvided ?? false,
    eligibleSites: options.eligibleSites,
  });
}

/** True when intersection has multiple sites, no configured Site Select, but Job Select exists for the actor. */
export function needsSyntheticSiteSelect(
  fields: RequestFieldLike[] | null | undefined,
  eligibleSites: FormSelectionSite[],
  siteFieldProctorProvided: boolean,
  isProctorContext = false,
  cttVirtualSiteCount?: number
): boolean {
  if (cttVirtualSiteCount != null && cttVirtualSiteCount <= 1) {
    return false;
  }
  if (eligibleSites.length <= 1) {
    return false;
  }
  if (actorHasSiteSelectField(fields, siteFieldProctorProvided)) {
    return false;
  }
  const jobProctorProvided = isProctorContext || siteFieldProctorProvided;
  return actorHasJobSelectField(fields, jobProctorProvided);
}

export const NO_SITES_FOR_JOB_SELECTION_MESSAGE = "No sites available for job selection";

export function getNoSitesForJobErrorMessage(
  fields: RequestFieldLike[] | null | undefined,
  eligibleSites: FormSelectionSite[],
  siteFieldProctorProvided: boolean
): string {
  if (eligibleSites.length > 0) {
    return "";
  }
  return actorHasJobSelectField(fields, siteFieldProctorProvided)
    ? NO_SITES_FOR_JOB_SELECTION_MESSAGE
    : "";
}

export function buildSyntheticSiteSelectField(proctorProvided: boolean): RequestFieldLike & { synthetic?: boolean } {
  return {
    fieldElementName: "Site Select",
    fieldId: 7,
    required: true,
    proctorProvided,
    synthetic: true,
  };
}

export function insertSyntheticSiteSelectBeforeJob(
  fields: RequestFieldLike[],
  siteFieldProctorProvided: boolean
): RequestFieldLike[] {
  const jobIndex = fields.findIndex(
    (field) =>
      field.proctorProvided === siteFieldProctorProvided &&
      isJobRequestField(field)
  );
  if (jobIndex < 0) {
    return fields;
  }
  const jobField = fields[jobIndex];
  const jobSequence = Number(jobField?.sequence ?? 0);
  const prevSequence =
    jobIndex > 0 ? Number(fields[jobIndex - 1]?.sequence ?? jobSequence) : null;
  let syntheticSequence: number;
  if (prevSequence != null && prevSequence < jobSequence) {
    syntheticSequence = prevSequence + (jobSequence - prevSequence) / 2;
  } else if (jobSequence > 0) {
    syntheticSequence = jobSequence - 1;
  } else {
    syntheticSequence = -1;
  }
  const synthetic = {
    ...buildSyntheticSiteSelectField(siteFieldProctorProvided),
    sequence: syntheticSequence,
  };
  return [...fields.slice(0, jobIndex), synthetic, ...fields.slice(jobIndex)];
}

export function testTypeHasProctorProvidedJobField(
  testType: { fields?: RequestFieldLike[] | null } | null | undefined
): boolean {
  return (testType?.fields ?? []).some(isProctorProvidedJobRequestField);
}

export function isDepartmentRequestField(field: RequestFieldLike): boolean {
  const fieldId = Number(field.fieldId ?? 0);
  const name = String(field.fieldElementName ?? "");
  return name === "Department Select" || fieldId === 23;
}

export type DepartmentCatalogItem = {
  id?: number;
  name?: string;
  active?: boolean;
  Active?: boolean;
};

export type DepartmentCttLink = {
  companyTestTypeId?: number;
  CompanyTestTypeId?: number;
  departmentId?: number;
  DepartmentId?: number;
};

export function testTypeHasJobSelectField(
  fields: RequestFieldLike[] | null | undefined
): boolean {
  return (fields ?? []).some((field) => isJobRequestField(field));
}

export function getActiveDepartmentsLinkedToCtt(
  departments: DepartmentCatalogItem[] | null | undefined,
  testTypeDepartments: DepartmentCttLink[] | null | undefined,
  companyTestTypeId: number | null | undefined
): FormSelectionDepartment[] {
  const cttId = Number(companyTestTypeId ?? 0);
  if (cttId <= 0) {
    return [];
  }

  const linkedIds = new Set(
    (testTypeDepartments ?? [])
      .filter((link) => Number(link.companyTestTypeId ?? link.CompanyTestTypeId) === cttId)
      .map((link) => Number(link.departmentId ?? link.DepartmentId))
      .filter((id) => id > 0)
  );

  return (departments ?? [])
    .filter((d) => {
      const id = Number(d.id);
      const active = d.active ?? d.Active ?? true;
      return active && linkedIds.has(id);
    })
    .map((d) => ({ id: Number(d.id), name: String(d.name ?? "") }))
    .filter((d) => d.id > 0 && d.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export type ResolveDepartmentSelectOptionsArgs = {
  companyTestTypeId: number | null | undefined;
  cttFields: RequestFieldLike[] | null | undefined;
  departments: DepartmentCatalogItem[] | null | undefined;
  testTypeDepartments: DepartmentCttLink[] | null | undefined;
  companySiteRoleJobs: CompanySiteRoleJobRow[] | null | undefined;
  filterOptions: FormSelectionFilterOptions;
  companyJobs: CompanyJobLookup[] | null | undefined;
};

export function resolveDepartmentSelectOptions(
  args: ResolveDepartmentSelectOptionsArgs
): FormSelectionDepartment[] {
  const cttId = Number(args.companyTestTypeId ?? 0);
  if (cttId <= 0) {
    return [];
  }

  const linked = getActiveDepartmentsLinkedToCtt(
    args.departments,
    args.testTypeDepartments,
    cttId
  );

  if (!testTypeHasJobSelectField(args.cttFields)) {
    return linked;
  }

  const linkedIds = new Set(linked.map((d) => d.id));
  const cttDepartmentIds =
    args.filterOptions.cttDepartmentIds?.filter((id) => id > 0) ??
    Array.from(linkedIds);

  return getDepartmentsFromEligibleJobs(
    args.companySiteRoleJobs,
    { ...args.filterOptions, companyTestTypeId: cttId, cttDepartmentIds },
    args.companyJobs,
    args.departments
  ).filter((d) => linkedIds.has(d.id));
}

export type DepartmentSelectUiMode =
  | "disabled-no-ctt"
  | "hidden"
  | "error-no-departments"
  | "single-locked"
  | "multi";

export const NO_DEPARTMENTS_FOR_TEST_TYPE_MESSAGE = "This test type has no departments";

export function getDepartmentSelectUiMode(args: {
  hasCtt: boolean;
  optionCount: number;
  required: boolean;
}): DepartmentSelectUiMode {
  if (!args.hasCtt) {
    return "disabled-no-ctt";
  }
  if (args.optionCount <= 0) {
    return args.required ? "error-no-departments" : "hidden";
  }
  if (args.optionCount === 1) {
    return "single-locked";
  }
  return "multi";
}

export function hasRequiredJobField(
  fields: RequestFieldLike[] | null | undefined,
  options: { takerOnly?: boolean; proctorProvided?: boolean } = {}
): boolean {
  if (!Array.isArray(fields)) {
    return false;
  }
  return fields.some((field) => {
    if (options.takerOnly && field.proctorProvided) {
      return false;
    }
    if (options.proctorProvided != null && field.proctorProvided !== options.proctorProvided) {
      return false;
    }
    return field.required && isJobRequestField(field);
  });
}

export function hasRequiredJobFieldForActor(
  fields: RequestFieldLike[] | null | undefined,
  proctorProvided: boolean
): boolean {
  return hasRequiredJobField(fields, { proctorProvided });
}
